import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Alert,
  App,
  Button,
  Drawer,
  Flex,
  Form,
  Select,
  Space,
  Typography
} from 'antd'
import { useTranslation } from 'react-i18next'

import {
  organizationsRolesDestroyMutation,
  organizationsRolesListOptions,
  organizationsRolesListQueryKey,
  organizationsRolesReplaceCreateMutation,
  organizationsRolesRetrieveOptions,
  type OrganizationRoleFull
} from 'src/api/main'
import { useInvalidatePredicateQueries } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

const { Text } = Typography

type Props = {
  open: boolean
  role: OrganizationRoleFull | null
  applicationId?: number
  organizationId: number
  onClose: () => void
}

export const DeleteRoleDrawer = ({
  open,
  role,
  applicationId,
  organizationId,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const [form] = Form.useForm<{ replacementRoleId?: number }>()

  const { data: roleDetails, isLoading: isLoadingRoleDetails } = useQuery({
    ...organizationsRolesRetrieveOptions({
      path: { id: Number(role?.id) }
    }),
    enabled: open && !!role?.id
  })

  const { data: rolesData } = useQuery({
    ...organizationsRolesListOptions({
      query: {
        application: applicationId,
        organization: organizationId,
        per_page: 100
      }
    }),
    enabled: open && !!applicationId
  })

  const usersCount = Number(roleDetails?.users_count ?? 0)
  const replacementRoleId = Form.useWatch('replacementRoleId', form)

  const replacementOptions =
    rolesData?.results
      ?.filter((r) => r.id !== role?.id)
      ?.map((r) => ({ value: r.id, label: r.name })) ?? []

  const { mutate: deleteRole, isPending: isDeleting } = useMutation({
    ...organizationsRolesDestroyMutation(),
    onSuccess: async () => {
      await invalidateQueries([organizationsRolesListQueryKey()])
      message.success(t('workspace.apps.roles.roleDeleted'))
      form.resetFields()
      onClose()
    },
    onError: (error) => showApiErrors(error)
  })

  const { mutate: replaceRole, isPending: isReplacing } = useMutation({
    ...organizationsRolesReplaceCreateMutation(),
    onSuccess: () => {
      if (role) {
        deleteRole({ path: { id: role.id } })
      }
    },
    onError: (error) => showApiErrors(error)
  })

  const onConfirm = async () => {
    if (!role) return
    if (usersCount > 0) {
      const values = await form.validateFields()
      replaceRole({
        path: { id: role.id },
        body: { replacement_role: Number(values.replacementRoleId) }
      })
      return
    }
    deleteRole({ path: { id: role.id } })
  }

  const canConfirm = usersCount === 0 || !!replacementRoleId

  return (
    <Drawer
      title={t('workspace.apps.roles.deleteRoleConfirm')}
      open={open}
      size={520}
      destroyOnHidden
      onClose={onClose}
      extra={
        <Space>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button
            danger
            type="primary"
            disabled={!canConfirm}
            loading={isDeleting || isReplacing}
            onClick={() => void onConfirm()}
          >
            {t('delete')}
          </Button>
        </Space>
      }
    >
      <Flex vertical gap={12}>
        <Alert
          type="warning"
          showIcon
          title={t('workspace.apps.roles.deleteRoleWarning')}
          description={
            <Space vertical size={2}>
              <Text>
                {t('workspace.apps.roles.deletingRole')}:{' '}
                <strong>{role?.name}</strong>
              </Text>
              {isLoadingRoleDetails ? (
                <Text type="secondary">{t('loading')}…</Text>
              ) : (
                <Text>
                  {t('workspace.apps.roles.usersWithRole', {
                    count: usersCount
                  })}
                </Text>
              )}
            </Space>
          }
        />
        {usersCount > 0 ? (
          <Form layout="vertical" form={form}>
            <Form.Item
              name="replacementRoleId"
              label={t('workspace.apps.roles.selectReplacementRole')}
              extra={t('workspace.apps.roles.replacementRoleDescription')}
              rules={[{ required: true, message: t('required_field_error') }]}
            >
              <Select
                placeholder={t('workspace.apps.roles.selectRolePlaceholder')}
                options={replacementOptions}
              />
            </Form.Item>
          </Form>
        ) : null}
      </Flex>
    </Drawer>
  )
}
